import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { isInternal } from "@/lib/api";
import { coaName } from "@/lib/masterI18n";
import { tx, stLabel, srcLabel } from "@/lib/txnI18n";
import { fmtRp } from "@/components/AmountInput";
import { DateInput, fmtDate } from "@/components/DateInput";
import { StatusBadge } from "@/components/StatusBadge";
import { TransactionForm } from "@/components/TransactionForm";
import { TransactionDetailPanel } from "@/components/TransactionDetailPanel";
import { ExcelImportModal } from "@/components/ExcelImportModal";
import { TxnBulkBar } from "@/components/txn/TxnBulkBar";
import { AutoJournalDialog } from "@/components/txn/AutoJournalDialog";
import { TaxIdBanner } from "@/components/txn/TaxIdBanner";
import { exportRows } from "@/components/TableTools";
import { toast } from "sonner";
import { fl } from "@/lib/finI18n";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileSpreadsheet, Search, Filter, AlertTriangle, Paperclip, Download, ArrowDownWideNarrow, ArrowUpNarrowWide } from "lucide-react";

const STATUSES = ["draft", "review", "checked", "revisi", "approved", "archived"];

export default function Transactions() {
  const { user, selectedClientId } = useAuth();
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const clientId = isInternal(user.role) ? selectedClientId : user.client_id;

  const [data, setData] = useState({ items: [], total: 0 });
  const [status, setStatus] = useState(params.get("status") || "all");
  const [projectId, setProjectId] = useState("all");
  const [search, setSearch] = useState("");
  const [searchQ, setSearchQ] = useState("");
  useEffect(() => { const h = setTimeout(() => setSearchQ(search.trim()), 350); return () => clearTimeout(h); }, [search]);
  const [year, setYear] = useState("all");
  const [coaId, setCoaId] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [projects, setProjects] = useState([]);
  const [coaList, setCoaList] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [editTxn, setEditTxn] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [sortDir, setSortDir] = useState("desc");
  const [autoRow, setAutoRow] = useState(null);

  const isClient = !isInternal(user.role);

  const query = useCallback((pg, limit) => {
    const p = new URLSearchParams({ page: pg, limit, sort: sortDir });
    if (isInternal(user.role)) p.set("client_id", clientId);
    if (status !== "all") p.set("status", status);
    if (projectId !== "all") p.set("project_id", projectId);
    if (searchQ) p.set("search", searchQ);
    if (year !== "all") p.set("year", year);
    if (coaId !== "all") p.set("coa_id", coaId);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    return `/transactions?${p.toString()}`;
  }, [clientId, status, projectId, searchQ, year, coaId, dateFrom, dateTo, sortDir, user.role]);

  const load = useCallback(async () => {
    if (!clientId) return;
    const { data } = await api.get(query(page, 50));
    setData(data);
    setSelected((s) => s.filter((id) => data.items.some((x) => x.id === id)));
  }, [clientId, query, page]);

  const exportTxns = async () => {
    let rows = data.items.filter((x) => selected.includes(x.id));
    if (!rows.length) rows = (await api.get(query(1, 20000))).data.items;
    exportRows(rows, [["Tanggal", "date"], ["Invoice", "invoice_no"], ["Vendor", "vendor"], ["Sumber Dana", "bank_name"], ["COA", (x) => `${x.coa_code || ""} ${coaName({ name: x.coa_name, name_en: x.coa_name_en, name_zh: x.coa_name_zh }, lang) || ""}`.trim()],
      ["Project", "project_name"], ["Arus", (x) => (x.flow === "in" ? "Masuk" : x.flow === "journal" ? "Jurnal" : "Keluar")], ["Nominal", "amount"], ["PPN", "ppn_amount"], ["PPh", "pph_amount"],
      ["Deskripsi", "description"], ["Status", (x) => stLabel(x.status, lang)], ["Sumber", (x) => (x.auto ? `${tx("auto", lang)} · ${srcLabel(x.source, x.source_label, lang)}` : tx("txn", lang))]], "transaksi");
    toast.success(`${rows.length} ${tx("exported", lang)}`);
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);
  useEffect(() => {
    const txn = params.get("txn");
    if (txn) { setDetailId(txn); setDetailOpen(true); }
  }, [params]);
  useEffect(() => {
    if (clientId) {
      api.get(`/projects?client_id=${clientId}`).then(({ data }) => setProjects(data));
      api.get(`/coa?client_id=${clientId}`).then(({ data }) => setCoaList(data));
    }
  }, [clientId]);

  const openDetail = (id) => { setDetailId(id); setDetailOpen(true); };
  const openEdit = (t) => { setEditTxn(t); setDetailOpen(false); setFormOpen(true); };
  const resetFilters = () => { setStatus("all"); setProjectId("all"); setSearch(""); setYear("all"); setCoaId("all"); setDateFrom(""); setDateTo(""); setParams({}); };

  const years = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);
  const pageIds = data.items.filter((x) => !x.auto).map((x) => x.id);
  const allSel = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const toggleAll = (v) => setSelected(v ? pageIds : []);
  const toggleOne = (id, v) => setSelected((s) => (v ? [...new Set([...s, id])] : s.filter((x) => x !== id)));
  useEffect(() => { setSelected([]); }, [clientId, page, status, projectId, searchQ, year, coaId, dateFrom, dateTo]);

  return (
    <div className="space-y-4" data-testid="transactions-page">
      <div className="page-sticky space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight">{t("txn.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{data.total} {t("txn.title").toLowerCase()}{data.auto_total ? ` · ${tx("incl", lang)} ${data.auto_total} ${tx("autoJ", lang)}` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportTxns} data-testid="txn-export">
            <Download className="h-4 w-4 mr-2" /> {selected.length ? `${tx("exportSel", lang)} (${selected.length})` : tx("exportBtn", lang)}
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-btn">
            <FileSpreadsheet className="h-4 w-4 mr-2" /> {t("txn.importExcel")}
          </Button>
          <Button onClick={() => { setEditTxn(null); setFormOpen(true); }} data-testid="add-transaction-btn">
            <Plus className="h-4 w-4 mr-2" /> {t("txn.new")}
          </Button>
        </div>
      </div>

      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder={t("txn.searchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="filter-search" />
          </div>
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger className="w-36" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{tx("allStatus", lang)}</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{stLabel(s, lang)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={projectId} onValueChange={(v) => { setProjectId(v); setPage(1); }}>
            <SelectTrigger className="w-40" data-testid="filter-project"><SelectValue placeholder={tx("project", lang)} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{tx("allProject", lang)}</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={year} onValueChange={(v) => { setYear(v); setPage(1); }}>
            <SelectTrigger className="w-28" data-testid="filter-year"><SelectValue placeholder={tx("year", lang)} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{tx("all", lang)}</SelectItem>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={coaId} onValueChange={(v) => { setCoaId(v); setPage(1); }}>
            <SelectTrigger className="w-44" data-testid="filter-coa"><SelectValue placeholder="Ledger / COA" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{tx("allCoa", lang)}</SelectItem>
              {coaList.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} · {coaName(c, lang)}</SelectItem>)}
            </SelectContent>
          </Select>
          <DateInput value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="w-40" data-testid="filter-date-from" title={tx("dateFrom", lang)} />
          <DateInput value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="w-40" data-testid="filter-date-to" title={tx("dateTo", lang)} />
          <Button variant="outline" size="sm" onClick={() => { setSortDir((s) => (s === "desc" ? "asc" : "desc")); setPage(1); }} data-testid="txn-sort-date">
            {sortDir === "asc" ? <ArrowUpNarrowWide className="h-4 w-4 mr-1" /> : <ArrowDownWideNarrow className="h-4 w-4 mr-1" />}{tx(sortDir === "asc" ? "oldest" : "newest", lang)}
          </Button>
          <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="reset-filters"><Filter className="h-4 w-4 mr-1" /> {tx("reset", lang)}</Button>
        </div>
      </Card>
      </div>

      <TaxIdBanner clientId={clientId} lang={lang} reloadKey={data} onSaved={load} />
      <TxnBulkBar clientId={clientId} ids={selected} coaList={coaList} onClear={() => setSelected([])} onDone={() => { setSelected([]); load(); }} />

      <Card className="overflow-hidden">
        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead className="w-10"><Checkbox checked={allSel} onCheckedChange={(v) => toggleAll(!!v)} aria-label={fl("selectAllPage", lang)} title={fl("selectAllPage", lang)} data-testid="txn-select-all" /></TableHead>
                <TableHead>{tx("date", lang)}</TableHead>
                <TableHead>{tx("invoice", lang)}</TableHead>
                <TableHead>{tx("vendor", lang)}</TableHead>
                <TableHead>COA</TableHead>
                <TableHead>{tx("project", lang)}</TableHead>
                <TableHead className="text-right">{tx("amount", lang)}</TableHead>
                <TableHead>{tx("status", lang)}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">Tidak ada transaksi</TableCell></TableRow>
              )}
              {data.items.map((t) => (t.auto ? (
                <TableRow key={t.id} className="cursor-pointer bg-secondary/20 hover:bg-secondary/40" onClick={() => setAutoRow(t)} data-testid={`txn-row-${t.id}`}>
                  <TableCell />
                  <TableCell className="num text-xs">{fmtDate(t.date)}</TableCell>
                  <TableCell className="text-xs font-medium">{t.invoice_no || "—"}</TableCell>
                  <TableCell className="text-xs max-w-[160px] truncate">{t.vendor || t.description || "—"}</TableCell>
                  <TableCell className="text-xs">{t.coa_code} {coaName({ name: t.coa_name, name_en: t.coa_name_en, name_zh: t.coa_name_zh }, lang)}
                    <span className="ml-1.5 inline-block rounded bg-primary/10 px-1.5 text-[10px] font-medium text-primary" data-testid={`txn-auto-${t.id}`}>{tx("auto", lang)} · {srcLabel(t.source, t.source_label, lang)}</span>
                    {t.bank_name && <span className="block text-[10px] text-muted-foreground">{tx(t.flow === "in" ? "inTo" : "outFrom", lang)} {t.bank_name}</span>}
                  </TableCell>
                  <TableCell className="text-xs">—</TableCell>
                  <TableCell className={`num text-right text-xs font-semibold ${t.flow === "in" ? "text-emerald-600" : t.flow === "out" ? "text-rose-600" : ""}`}>
                    {t.flow === "in" ? "+" : t.flow === "out" ? "−" : ""}{fmtRp(t.amount, lang)}
                  </TableCell>
                  <TableCell><StatusBadge status={t.status} /></TableCell>
                  <TableCell />
                </TableRow>
              ) : (
                <TableRow key={t.id} className={`cursor-pointer hover:bg-secondary/40 ${selected.includes(t.id) ? "bg-primary/5" : ""}`} onClick={() => openDetail(t.id)} data-testid={`txn-row-${t.id}`}>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.includes(t.id)} onCheckedChange={(v) => toggleOne(t.id, !!v)} data-testid={`txn-select-${t.id}`} />
                  </TableCell>
                  <TableCell className="num text-xs">{fmtDate(t.date)}</TableCell>
                  <TableCell className="text-xs font-medium">{t.invoice_no || "—"}</TableCell>
                  <TableCell className="text-xs max-w-[160px] truncate">{t.vendor || "—"}</TableCell>
                  <TableCell className="text-xs">{t.coa_code} {coaName({ name: t.coa_name, name_en: t.coa_name_en, name_zh: t.coa_name_zh }, lang)}
                    {t.lines?.length > 1 && (
                      <span className="ml-1.5 inline-block rounded border border-primary/30 bg-primary/5 px-1.5 text-[10px] text-primary" data-testid={`txn-lines-${t.id}`}>+{t.lines.length - 1} {tx("rows", lang)}</span>
                    )}
                    {(t.kind === "purchase" || t.kind === "sales" || t.source === "invoice_payment") && (
                      <span className="ml-1.5 inline-block rounded border border-border px-1.5 text-[10px] text-muted-foreground" data-testid={`txn-kind-${t.id}`}>{t.kind === "purchase" ? "Purchased" : "Sales"}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{t.project_name || "—"}</TableCell>
                  <TableCell className={`num text-right text-xs font-semibold ${t.flow === "in" ? "text-emerald-600" : "text-rose-600"}`}>
                    {t.flow === "in" ? "+" : "−"}{fmtRp(t.amount, lang)}
                  </TableCell>
                  <TableCell><StatusBadge status={t.status} /></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-muted-foreground">
                      {t.pending_document && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                      {t.tax_incomplete && (
                        <span className="whitespace-nowrap rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 text-[10px] font-medium text-amber-800 dark:text-amber-300" title={fl("taxIdMissing", lang)} data-testid={`txn-taxid-missing-${t.id}`}>{fl("taxIdShort", lang)}</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {data.total > 50 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Halaman {page} dari {Math.ceil(data.total / 50)}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Sebelumnya</Button>
            <Button variant="outline" size="sm" disabled={page >= Math.ceil(data.total / 50)} onClick={() => setPage((p) => p + 1)}>Berikutnya</Button>
          </div>
        </div>
      )}

      <TransactionForm open={formOpen} onOpenChange={setFormOpen} clientId={clientId} editTxn={editTxn} onSaved={load} />
      <ExcelImportModal open={importOpen} onOpenChange={setImportOpen} clientId={clientId} onImported={load} />
      <TransactionDetailPanel txnId={detailId} open={detailOpen} onOpenChange={setDetailOpen} onChanged={load} onEdit={openEdit} />
      <AutoJournalDialog row={autoRow} onClose={() => setAutoRow(null)} />
    </div>
  );
}
